import { FC, useEffect, useState } from "react";
import { ListItem } from "@mui/material";
import "@/components/navigation/navigationBar.css";

import {
  NavigationNavLink,
  NavigationItemButton,
  NavigationListItemContent,
} from "@/components/navigation/navigationItems";
import { useTranslation } from "react-i18next";
import Restricted from "@/consumer/restricted";
import GenericExpandableNavigationListItem from "@/components/navigation/genericExpandableNavigationListItem";
import GenericNavigationCollapse from "@/components/navigation/genericNavigationCollapse";
import {
  managerLmsSubRoutes,
  managerMarketingLmsSubRoutes,
} from "@/components/navigation/managerSubRoutes";
import { COURSE_ACCESS_PERMISSION } from "@/components/lms/lms.utils";
import { useManagerLmsAccess } from "@/context/managerLmsAccessContext";

interface Props {
  drawerOpen: boolean;
  selectedIndex: number;
  handleListItemClick?: any;
  handleListSoubrouteItemClick?: any;
  subdomain: string;
  subRouteToOpen?: string;
}

const ManagerRoutes: FC<Props> = ({
  drawerOpen,
  selectedIndex,
  handleListItemClick,
  handleListSoubrouteItemClick,
  subdomain,
  subRouteToOpen,
}) => {
  const { t } = useTranslation();
  const { mode: lmsAccessMode } = useManagerLmsAccess();
  const [subroutesOpen, setSubroutesOpen] = useState<any>({});
  const handleSubrouteClick =
    handleListSoubrouteItemClick ?? handleListItemClick ?? (() => undefined);

  const toggleSubrouteOpen = (routeId: any) => {
    setSubroutesOpen((prevState: any) => ({
      ...prevState,
      [routeId]: !prevState[routeId],
    }));
  };

  useEffect(() => {
    if (!subRouteToOpen) return;
    setSubroutesOpen((prevState: any) => ({
      ...prevState,
      [subRouteToOpen]: true,
    }));
  }, [subRouteToOpen]);

  if (subdomain === "manage") {
    return (
      <>
        <ListItem
          disablePadding
          className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
        >
          <NavigationNavLink to={"/mitarbeiter"}>
            <NavigationItemButton
              drawerOpen={drawerOpen}
              onClick={(event: any) => handleListItemClick(event, 10)}
              selected={selectedIndex === 10}
            >
              <NavigationListItemContent
                drawerOpen={drawerOpen}
                primaryText={t("navigation_manage_customer")}
                icon="employee"
              />
            </NavigationItemButton>
          </NavigationNavLink>
        </ListItem>

        <ListItem
          disablePadding
          className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
        >
          <NavigationNavLink to="appointmentrequestopen">
            <NavigationItemButton
              drawerOpen={drawerOpen}
              onClick={(event: any) => handleListItemClick(event, 11)}
              selected={selectedIndex === 11}
            >
              <NavigationListItemContent
                drawerOpen={drawerOpen}
                primaryText={t("navigation_manage_appointments")}
                icon="appointments"
              />
            </NavigationItemButton>
          </NavigationNavLink>
        </ListItem>
        <ListItem
          disablePadding
          className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
        >
          <NavigationNavLink to="buildings">
            <NavigationItemButton
              drawerOpen={drawerOpen}
              onClick={(event: any) => handleListItemClick(event, 12)}
              selected={selectedIndex === 12}
            >
              <NavigationListItemContent
                drawerOpen={drawerOpen}
                primaryText={t("buildings")}
                icon="building"
              />
            </NavigationItemButton>
          </NavigationNavLink>
        </ListItem>
        <Restricted permissionRequired="frontend_view_appointment_archive">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="appointmentArchive">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 13)}
                selected={selectedIndex === 13}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("appointment_archive")}
                  icon="bookedAppointments"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_accidentbook">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/accidentBook">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 15)}
                selected={selectedIndex === 15}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("accidentbook")}
                  icon="accidentbook"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_preventive_care">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/preventiveCareList">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 14)}
                selected={selectedIndex === 14}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("preventive_care")}
                  icon="preventiveCare"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        {(lmsAccessMode === "full" || lmsAccessMode === "marketing") && (
          <Restricted permissionRequired={COURSE_ACCESS_PERMISSION}>
            <GenericExpandableNavigationListItem
              listItemIconName="lms"
              listItemLabel={t("lms_product_name")}
              drawerOpen={drawerOpen}
              handleSubrouteOpen={toggleSubrouteOpen}
              subRouteOpen={subroutesOpen}
              subRouteKey="klearning"
            />
            <GenericNavigationCollapse
              routesToRender={
                lmsAccessMode === "marketing"
                  ? managerMarketingLmsSubRoutes
                  : managerLmsSubRoutes
              }
              drawerOpen={drawerOpen}
              selectedIndex={selectedIndex}
              subRouteOpen={subroutesOpen}
              handleListSoubrouteItemClick={handleSubrouteClick}
              subRouteKey="klearning"
            />
          </Restricted>
        )}
      </>
    );
  } else return <></>;
};

export default ManagerRoutes;
